import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { confirmSignIn, signIn, getCurrentUser, signOut } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { AuditDetails, LoginType } from '@models/registration.models';
import { PreferencesService } from './preferences.service';
import { relationshipOptions, relationshipOptionsFromRoles, StakeholderRoleDto } from '../constants/app.constants';

export interface UserDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyId?: string;
  companyName?: string;
  companyType?: string; // Added to store company type
  isVerified: boolean;
  role?: string;
  verificationStatus: string;
}

export interface JwtPayload {
  email: string;
  authChallenge: string;
  meterNumber?: string;
  agentOrgId?: string;
  agentOrg?: string;
  consentRequestId: string;
  agentEmail?: string;
  // Add other fields if necessary
}

export interface CognitoAuthResult {
  success: boolean;
  userDetails?: UserDetails;
  error?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private router = inject(Router);
  private http = inject(HttpClient);
  private _loginUser: UserDetails | null = null;
  private _isLoggedIn = new BehaviorSubject<boolean>(false);
  private prefereneces = inject(PreferencesService);

  constructor() {
    // Initialize from Cognito session on startup
    this.initializeFromCognito();
  }

  // Getter for loginUser property
  get loginUser(): UserDetails | null {
    return this._loginUser;
  }

  // Getter for loginUserEmail property
  get loginUserEmail(): Observable<string> {
    return from((async () => {
      const currentUser = await getCurrentUser();
      return currentUser?.signInDetails?.loginId || '';
    })());
  }

  private async initializeFromCognito(): Promise<void> {
    try {
      const isSignedIn = await this.isCognitoUserSignedIn();
      if (isSignedIn) {

        // TODO: Debug this later to see if we can get the current user directly from Cognito.
      //  const email = localStorage.getItem('logged_in_email');
        const currentUser = await getCurrentUser();
        const email = currentUser.signInDetails?.loginId || currentUser.username;
        
        if (email) {
          const userDetails = await this.getUserDetails(email);
          this._loginUser = userDetails;
          this._isLoggedIn.next(userDetails.isVerified);
          console.log('User initialized from Cognito:', userDetails);
        }
      } else {
        this._isLoggedIn.next(false);
      }
    } catch (error) {
      console.error('Error initializing from Cognito:', error);
      this._isLoggedIn.next(false);
    }
  }

  // Method to check if user is logged in
  isLoggedIn(): Observable<boolean> {
    return this._isLoggedIn.asObservable();
  }

  /**
   * Async method to check if user is logged in with proper Cognito verification
   * This should be used by guards and other places that need to wait for auth verification
   * @returns Promise<boolean> indicating if user is authenticated
   */
  async isLoggedInAsync(): Promise<boolean> {
    try {
      return await this.isCognitoUserSignedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  // Method to handle logout
  async logout(redirect = true): Promise<void> {
    try {
      // Sign out from Cognito
      await signOut();
      console.log('Successfully signed out from Cognito');
    } catch (error) {
      console.error('Error signing out from Cognito:', error);
      // Continue with local logout even if Cognito signout fails
    }

  //  localStorage.removeItem('logged_in_email');
    // Clear local state
    this._isLoggedIn.next(false);
    this._loginUser = null;
    
    console.log('User logged out');
    if (redirect) {
      this.router.navigate(['/landing']);
    }
  }

  /**
   * Deletes the current user account
   * @returns Observable<boolean> indicating whether account deletion was successful
   */
  deleteAccount(): Observable<boolean> {
    const url = `${environment.baseUrl}/v1/delete-user`;
    
    return this.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('Cannot delete account: No user email available');
          return of(false);
        }
        
        console.log(`Deleting account for: ${email}`);
        
        return this.http.delete<any>(url, {
          headers: {
            'Content-Type': 'application/json'
          },
          body: {
            email: email
          }
        }).pipe(
          map(async (response) => {
            console.log('Account deleted successfully');
            // Use the existing logout method to properly clean up Cognito session and local state
            await this.logout();
            return true;
          }),
          // Convert the Promise<boolean> back to Observable<boolean>
          switchMap(promiseResult => from(promiseResult)),
          catchError(error => {
            console.error('Error deleting account:', error);
            return of(false);
          })
        );
      }),
      catchError(error => {
        console.error('Error getting user email for account deletion:', error);
        return of(false);
      })
    );
  }

  recordLogin(userDetails: UserDetails): void {
    console.log(`Logging in user: ${userDetails.firstName} (${userDetails.email})`);
    this._loginUser = userDetails;

   // if (userDetails.isVerified) {
      this._isLoggedIn.next(true);
  //  }
  }

  verifyOTP(otp: string, encryptedData: string): Observable<string> {
    const url = `${environment.baseUrl}/core/v1/verify-otp`;
    const payload = {
      encryptedData: encryptedData,
      otp: otp
    };
    
    console.log(`Verifying OTP for encrypted data: ${encryptedData.substring(0, 10)}...`);
    
    return this.http.post<any>(url, payload).pipe(
      map(response => {
        if (response.status === true && response.code === 200) {
          console.log('OTP verified successfully');
          return response.data.magicLink;
        } else {
          // Handle unexpected success response format
          throw new Error('Unexpected response format');
        }
      }),
      catchError(error => {
        console.error('Error verifying OTP:', error);
        
        // Handle HTTP error responses with our custom error structure
        if (error.error) {
          const errorResponse = error.error;
          
          switch (errorResponse.errorCode) {
            case 'INVALID_OTP':
              throw new Error('INVALID_OTP');
            case 'OTP_EXPIRED':
              throw new Error('OTP_EXPIRED');
            default:
              throw new Error(errorResponse.message || 'OTP verification failed');
          }
        }
        
        // Handle other types of errors (network, etc.)
        throw new Error('OTP verification failed');
      })
    );
  }

  sendLogin(email: string, magicLink: string, queryParams?: any): Observable<{loginType: LoginType, metadata: string | null}> {
    const loginType = this.prefereneces.getLoginType();

    if (loginType === LoginType.EMAIL_MAGIC_LINK) {
      return this.sendMagicLink(email, magicLink, queryParams).pipe(
        map(magicLink => {
          return { loginType: LoginType.EMAIL_MAGIC_LINK, metadata: magicLink };
        })
      );
    } else if (loginType === LoginType.EMAIL_OTP) {
      return this.sendOTP(email, magicLink, queryParams).pipe(
        map(encryptedData => {
          return { loginType: LoginType.EMAIL_OTP, metadata: encryptedData };
        })
      );
    } else if (loginType === LoginType.BYPASS_EMAIL_VERIFICATION) {
      // For bypass email verification, we directly return success without sending any email
      return this.getMagicLink(email, magicLink, queryParams).pipe(
        map(magicLink => {
          return { loginType: LoginType.BYPASS_EMAIL_VERIFICATION, metadata: magicLink };
        })
      );  
    } else if (loginType === LoginType.EMAIL_LINK_AND_OTP) {
      // For EMAIL_LINK_AND_OTP, we send only magic link
      return this.sendMagicLink(email, magicLink, queryParams).pipe(
        map(magicLink => {
          return { loginType: LoginType.EMAIL_MAGIC_LINK, metadata: magicLink };
        })
      );
    }

    return of({ loginType: LoginType.EMAIL_MAGIC_LINK, metadata: null });
  }


  /**
   * Builds the `custom` block of user-login (signed into the magic-link JWT).
   * Returns undefined when there is nothing to carry, as the API rejects null values.
   */
  private buildLoginCustom(queryParams?: any): { consentRequestId: string } | undefined {
    const consentRequestId = queryParams?.consentRequestId;
    return consentRequestId ? { consentRequestId } : undefined;
  }

  sendOTP(email: string, magicLink: string, queryParams?: any): Observable<string | null> {
    const url = `${environment.baseUrl}/core/v1/user-login`;
    const payload = {
      email: email.toLowerCase(),
      subject: 'Perse ConsentWallet Login',
      URL: magicLink,
      custom: this.buildLoginCustom(queryParams),
      type: "otp"
    };
    console.log(`Sending OTP to: ${email}`);
    return this.http.post<any>(url, payload).pipe(
      map(response => {
        console.log('OTP sent successfully');
        return response.data.encryptedData;
      }),
      catchError(error => {
        console.error('Error sending OTP:', error); // Log the error for debugging
        return of(null);
      })
    );
  }

  getMagicLink(email: string, magicLink: string, queryParams?: any): Observable<string | null> {
    const url = `${environment.baseUrl}/core/v1/user-login`;
    const payload = {
      email: email.toLowerCase(),
      subject: 'Perse ConsentWallet Login',
      URL: magicLink,
      custom: this.buildLoginCustom(queryParams),
      type: "url"
    };
    console.log(`Requesting magic link for: ${email}`);
    return this.http.post<any>(url, payload).pipe(
      map(response => {
        console.log('Magic link retrieved successfully');
        return response.data.url || null;
      }),
      catchError(error => {
        console.error('Error retrieving magic link:', error); // Log the error for debugging
        return of(null);
      })
    );
  }
  /**
   * Sends a magic link to the specified email address
   * @param email The email address to send the magic link to
   * @returns Observable<boolean> indicating whether the magic link was sent successfully
   */
  sendMagicLink(email: string, magicLink: string, queryParams?: any): Observable<string | null> {
    const url = `${environment.baseUrl}/core/v1/user-login`;
    
    // Build base URL for magic link redirect from environment
    let redirectUrl = magicLink
    
    const payload = {
      email: email.toLowerCase(),
      subject: 'Perse ConsentWallet Login',
      URL: redirectUrl,
      custom: this.buildLoginCustom(queryParams),
      type: "email"
    };
    
    console.log(`Sending magic link to: ${email}`);
    
    return this.http.post<any>(url, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        console.log('Magic link sent successfully');
        return response.data.message || null; // Return the message or null if not present
      }),
      catchError(error => {
        console.error('Error sending magic link:', error);
        return of(null);
      })
    );
  }
  
  /**
   * Registers a new user with the provided details
   * @param userDetails The details of the user to register
   * @param isLoginUserUnverified Whether the login user is unverified
   * @returns Observable<boolean> indicating whether registration was successful
   */
  registerUser(userDetails: UserDetails, auditDetails: AuditDetails, verificationStatus = 'verified', isLoginUserUnverified = false, isSdkFlow = false, queryParams?: any): Observable<{loginType: LoginType, metadata: string | null}> {
    const url = `${environment.baseUrl}/core/v1/user-signup`;
    const payload = {
      userDetails: {
        email: userDetails.email,
        firstname: userDetails.firstName,
        lastname: userDetails.lastName,
        companyName: userDetails.companyName,
        companyNumber: userDetails.companyId,
        companyType: userDetails.companyType,
        phoneNumber: userDetails.phone,
        role: userDetails.role,
        status: verificationStatus, // Default to verified for registration
      },
      auditDetails: {
        ...auditDetails,
      }
    };
    
    console.log(`Registering user: ${userDetails.firstName} ${userDetails.lastName} (${userDetails.email})`);
    
    return this.http.post<any>(url, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        console.log('Registration successful');
        return true;
      }),
      switchMap(() => {
        console.log('Sending magic link to newly registered user');
        if (isLoginUserUnverified) {
          // If the user is unverified, we don't need to send a magic link to verify their email
          return of({loginType: LoginType.UNVERIFIED, metadata: 'User registered but not verified'});
        } else {
          let magicLink = environment.magicLinkRedirectUrl;
          if (isSdkFlow) {
            magicLink = environment.magicLinkSdk;
          }
          return this.sendLogin(userDetails.email, magicLink, queryParams);
        }
      })
    );
  }

  /**
   * Fetches user details for a specific email
   * @param email The email address of the user
   * @returns Promise<UserDetails> with the user's information
   */
  getUserDetails(email: string): Promise<UserDetails> {
    const url = `${environment.baseUrl}/core/v1/user`;
    
    console.log(`Fetching user details for: ${email}`);
    
    // Original API call implementation commented out:
    return firstValueFrom(this.http.get<any>(url, {
      params: { email: email.toLowerCase() }
    }).pipe(
      map(response => {
        // Transform the API response to match our UserDetails interface
        const userData = response.data;
        
        const userDetails: UserDetails = {
          firstName: userData.firstname || '',
          lastName: userData.lastname || '',
          email: userData.email || email,
          phone: userData.phoneNumber || '',
          companyId: userData.companyNumber || '',
          companyName: userData.companyName || '',
          companyType: userData.companyType || '', // Added to store company type
          role: userData.role || '',
          isVerified: userData.status !== 'unverified', // Convert status to boolean
          verificationStatus: userData.status
        };
        return userDetails;
      })
    ));
  }

  // ==================== COGNITO AUTHENTICATION METHODS ====================

  /**
   * Checks if user is already signed in with Cognito
   * @returns Promise<boolean> indicating if user is signed in
   */
  async isCognitoUserSignedIn(): Promise<boolean> {

    // Cognito's getCurrentUser method seems to be not integrated correctly.
    // TODO: Debug this later to see if we can get the current user directly from Cognito.

  //  return localStorage.getItem('logged_in_email') !== null;
    try {
      const currentUser = await getCurrentUser();
      return !!currentUser;
    } catch (error) {
      console.error('Error checking Cognito user status:', error);
      return false;
    }
  }

  /**
   * Decodes the 'link' parameter to extract the JWT payload.
   * @param link The Base64-encoded JWT string.
   * @returns The decoded JWT payload or null if decoding fails.
   */
  decodeAuthLink(link: string): JwtPayload | null {
    try {
      // Base64 decode
      const decodedLink = atob(link);

      // Parse JWT
      const decodedJwt = jwtDecode<JwtPayload>(decodedLink);

      return decodedJwt;
    } catch (error) {
      console.error('Error decoding auth link:', error);
      return null;
    }
  }

  /** Email of the current Cognito session, or undefined when it cannot be determined. */
  private async getSignedInEmail(): Promise<string | undefined> {
    try {
      const currentUser = await getCurrentUser();
      return currentUser.signInDetails?.loginId || currentUser.username;
    } catch (error) {
      console.error('Unable to read the signed-in Cognito user:', error);
      return undefined;
    }
  }

  /**
   * Authenticates user using Cognito with email and challenge response
   * @param email The user's email address
   * @param challengeResponse The challenge response from the JWT
   * @returns Promise<CognitoAuthResult> with authentication result
   */
  async authenticateWithCognito(email: string, challengeResponse: any): Promise<CognitoAuthResult> {
    try {
      // Check if user is already signed in
      let isSignedIn = await this.isCognitoUserSignedIn();
      if (isSignedIn) {
        // A link for a different user must not reuse someone else's session (it would skip the
        // unverified check and prefill, and mix identities). The link wins: drop the stale session.
        const signedInEmail = await this.getSignedInEmail();
        if (signedInEmail?.toLowerCase() !== email.toLowerCase()) {
          console.log('Signed-in user differs from the link email, signing out before using the link');
          await this.logout(false);
          isSignedIn = false;
        }
      }
      if (isSignedIn) {
        console.log('User is already signed in to Cognito');
        const userDetails = await this.getUserDetails(email);
        return { success: true, userDetails };
      }

      const userDetails = await this.getUserDetails(email);
      if (userDetails.isVerified === false) {
        // this.recordLogin(userDetails);
        return { success: false, error: 'UNVERIFIED' };
      }

      // Initiate sign-in with custom auth flow
      const { isSignedIn: signInSuccess, nextStep } = await signIn({
        username: email,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          preferredChallenge: challengeResponse
        }
      });

      if (!signInSuccess && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const response = await confirmSignIn({
          challengeResponse
        });

        // Cognito does not seem to return a direct success flag here
        // so we assume if we reach here, the sign-in was successful

        // Also storing the email in the localStorage 
       // localStorage.setItem('logged_in_email', email);

        if (response.isSignedIn) {
          console.log('User signed in successfully with Cognito');
          this.recordLogin(userDetails);
          return { success: true, userDetails };
        } else {
          return { success: false, error: 'Sign-in confirmation failed' };
        }
      } else if (signInSuccess) {
        console.log('User signed in successfully with Cognito');
        const userDetails = await this.getUserDetails(email);
        return { success: true, userDetails };
      } else {
        return { success: false, error: 'Sign-in failed' };
      }

    } catch (error: any) {
      console.error('Error during Cognito authentication:', error);

      // Handle specific Cognito errors
      if (error.code === 'UserNotFoundException') {
        return { success: false, error: 'User does not exist in Cognito' };
      } else {
        return { success: false, error: error.message || 'Authentication failed' };
      }
    }
  }

  /**
   * Complete authentication flow - decode link, authenticate with Cognito, and record login
   * @param link The Base64-encoded JWT string from the magic link
   * @returns Promise<CognitoAuthResult> with complete authentication result
   */
  async completeAuthenticationFlow(link: string): Promise<CognitoAuthResult & { 
    redirectInfo?: Partial<JwtPayload>
  }> {
    try {
      // 1. Decode the JWT link
      const decodedJwt = this.decodeAuthLink(link);

      if (!decodedJwt) {
        return { success: false, error: 'Failed to decode authentication link' };
      }

      const { email, authChallenge, consentRequestId} = decodedJwt;


      if (!email) {
        return { success: false, error: 'Email not found in authentication link' };
      }

      if (!authChallenge) {
        return { success: false, error: 'Challenge response not found in authentication link' };
      }

      // 2. Authenticate with Cognito
      const authResult = await this.authenticateWithCognito(email, authChallenge);
      if (!authResult.success || !authResult.userDetails) {
        return {...authResult, email, redirectInfo: { consentRequestId } };
      }

      // 4. Record the login
      // We will record the login inside authenticateWithCognito method
   //   this.recordLogin(authResult.userDetails);

      // 5. Return result with redirect information
      return {
        success: true,
        email,
        userDetails: authResult.userDetails,
        redirectInfo: { consentRequestId }
      };

    } catch (error: any) {
      console.error('Error in complete authentication flow:', error);
      return { success: false, error: error.message || 'SIGN_IN_FAILED' };
    }
  }

  /**
   * Refreshes user data from Cognito and updates local state
   * @returns Promise<boolean> indicating if refresh was successful
   */
  async refreshUserFromCognito(): Promise<boolean> {
    try {
      const isSignedIn = await this.isCognitoUserSignedIn();
      if (!isSignedIn) {
        this._isLoggedIn.next(false);
        this._loginUser = null;
        return false;
      }

      if (this._loginUser) {
        // If we already have a user, no need to refresh
        return true;
      }

      const currentUser = await getCurrentUser();
      const email = currentUser.signInDetails?.loginId || currentUser.username;
      
      if (email) {
        const userDetails = await this.getUserDetails(email);
        this._loginUser = userDetails;
        this._isLoggedIn.next(userDetails.isVerified);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error refreshing user from Cognito:', error);
      this._isLoggedIn.next(false);
      this._loginUser = null;
      return false;
    }
  }

  isCompanyUser(): boolean {
    return !!(this.loginUser?.companyId || this.loginUser?.companyName);
  }

  private readonly _relationshipOptions = signal(relationshipOptions);
  private relationshipOptionsRequested = false;

  /**
   * Relationship choices for the add-meter / consent flows. Starts as the built-in list and is replaced
   * once with the roles the admin dashboard marks visible in Consent Wallet; any failure keeps the built-in list.
   */
  getRelationshipOptions() {
    if (!this.relationshipOptionsRequested) {
      this.relationshipOptionsRequested = true;
      this.http
        .get<{ data?: StakeholderRoleDto[] }>(
          `${environment.baseUrlStakeholderRoles}/v1/stakeholder-roles/public?consentWalletVisibility=true`
        )
        .subscribe({
          next: res => {
            const fromRoles = relationshipOptionsFromRoles(res?.data ?? []);
            if (fromRoles.length > 0) this._relationshipOptions.set(fromRoles);
          },
          error: err => console.warn('Could not load stakeholder roles, using built-in relationship options', err),
        });
    }
    return this._relationshipOptions.asReadonly();
  }
}
